import { LRNSY_LEGAL } from "./legal.constants";
import type { LegalDocument } from "./legal.types";

export const PRIVACY_POLICY_DOCUMENT: LegalDocument = {
  slug: "privacy",
  title: "LRNSY Privacy Policy",
  subtitle:
    "How LRNSY collects, uses, protects, stores, and gives you control over personal and emotional data.",
  effectiveDate: LRNSY_LEGAL.effectiveDate,
  lastUpdated: LRNSY_LEGAL.lastUpdated,
  sections: [
    {
      id: "introduction",
      title: "1. Introduction",
      body: [
        "LRNSY is an interactive emotional experience platform operated by Electrixitaty Limited. LRNSY allows users to create, personalize, pay for, send, receive, reply to, replay, and interact with meaningful digital emotional experiences.",
        "These experiences may include private letters, recipient names, sender names, photographs, music, emotional archetypes, private shareable links, recipient replies, symbolic gifting features, reaction recordings, campaign experiences, ambassador links, creator-generated content, and other emotional storytelling tools that LRNSY may introduce over time.",
        "Because LRNSY may handle information that is highly personal, emotionally sensitive, private, intimate, or relationship-based, privacy, dignity, security, emotional safety, restraint, transparency, and responsible data handling are core principles of LRNSY.",
      ],
    },
    {
      id: "who-we-are",
      title: "2. Who We Are",
      body: [
        "LRNSY is operated by Electrixitaty Limited, a company based in Nairobi, Kenya.",
        "For purposes of this Privacy Policy, “LRNSY,” “we,” “us,” and “our” refer to Electrixitaty Limited and the LRNSY platform.",
        "Depending on the context, Electrixitaty Limited may act as a data controller where it determines the purposes and means of processing personal data, and may act as a data processor where it processes personal data on behalf of another party.",
      ],
    },
    {
      id: "privacy-commitment",
      title: "3. Our Privacy Commitment",
      body: [
        "Private emotional expression should be treated with exceptional care.",
        "LRNSY does not design its platform to sell private emotional messages, profile intimate feelings for advertising, or turn personal vulnerability into advertising inventory.",
      ],
      bullets: [
        "We minimize unnecessary data collection.",
        "We avoid using private letter content for advertising personalization.",
        "We separate emotional content from operational metadata where practical.",
        "We use encryption-first architecture for letters and sensitive message content where available.",
        "We restrict internal access to sensitive content.",
        "We provide meaningful user controls for privacy, deletion, consent, and local storage where technically possible.",
      ],
    },
    {
      id: "scope",
      title: "4. Scope of This Policy",
      body: [
        "This Privacy Policy applies to LRNSY’s website, web application, create flow, archetype selection, recipient experience pages, preview pages, payment pages, share pages, dashboard pages, ambassador pages, campaign pages, support communications, analytics systems, private link systems, reply flows, symbolic gifting features, reaction recording features where available, and future related services.",
      ],
    },
    {
      id: "data-we-collect",
      title: "5. Personal Data We May Collect",
      body: ["The data LRNSY processes depends on how you use the platform."],
      bullets: [
        "Account data, including name, email, profile photo, authentication provider details, user ID, role, login timestamps, account status, and preferences.",
        "Sender data, including sender name, selected archetype, selected campaign, message or letter, uploaded photo or media, payment status, draft status, share status, privacy mode selection, and device/session information.",
        "Recipient data, including recipient name, recipient photo if uploaded, recipient link ID, access status, open timestamp, completion status, replay activity, reply activity, device type, browser type, and security metadata.",
        "Experience content, including letters, emotional messages, personal notes, names, photographs, music selections, replies, symbolic gifting metadata, and campaign-specific messages.",
        "Payment data, including payment reference, transaction ID, amount, currency, status, date, provider reference, refund or dispute records, and fraud-prevention data.",
        "Technical data, including IP address, browser type, device type, operating system, screen size, time zone, referring URL, pages visited, clicks, error logs, performance logs, security logs, cookie identifiers, and local storage identifiers.",
        "Analytics and funnel data, including landing views, archetype views, create starts, step progression, payment initiation, payment success, share behavior, recipient opens, recipient completion, replay behavior, reply behavior, and recipient-to-sender conversion.",
      ],
    },
    {
      id: "sensitive-emotional-content",
      title: "6. Sensitive Emotional Content",
      body: [
        "LRNSY recognizes that some user content may be emotionally sensitive even where it does not fall into a formal legal category of sensitive data in every jurisdiction.",
        "Sensitive emotional content may include romantic messages, apologies, reconciliation attempts, grief-related messages, private memories, emotional support messages, family matters, private relationship context, and deeply vulnerable written expression.",
        "Users choose what they write, upload, send, or reply with. Users should avoid including information they do not have the right to share, including another person’s private details, images, likeness, or sensitive personal information.",
      ],
    },
    {
      id: "how-we-use-data",
      title: "7. How We Use Personal Data",
      body: [
        "LRNSY uses personal data to provide and secure the platform, process payments, render emotional experiences, support private links, enable replies, enable dashboards, prevent abuse, improve reliability, and comply with legal obligations.",
      ],
      bullets: [
        "To create and manage accounts.",
        "To authenticate users and support Google sign-in.",
        "To allow senders to create and share experiences.",
        "To allow recipients to open, replay, and reply to experiences.",
        "To generate private recipient links.",
        "To process payments, payment confirmations, refunds, and disputes.",
        "To detect suspicious activity, fraud, or abuse.",
        "To improve loading performance, emotional pacing, conversion clarity, and platform reliability.",
        "To send account notices, security alerts, support replies, and legal updates.",
      ],
    },
    {
      id: "privacy-modes",
      title: "8. Privacy Modes for Letters",
      body: [
        "LRNSY is designed to support two privacy models for sensitive written content: Standard Private Mode and Ultra Private Mode.",
        "Standard Private Mode is the default intended privacy model. It balances strong privacy, usability, account recovery, link regeneration, cross-device access, customer support practicality, and secure operational continuity.",
        "Ultra Private Mode is intended for users who want the strongest available privacy protection for letter content. In Ultra Private Mode, encryption may occur locally in the sender’s browser or device, LRNSY may store only encrypted ciphertext, and LRNSY may not retain a recoverable decryption key.",
      ],
      note: "Ultra Private Mode gives stronger privacy but may make recovery impossible if the private link, key, or passcode is lost.",
    },
    {
      id: "private-links",
      title: "9. Private Links and Recipient Access",
      body: [
        "LRNSY experiences are generally delivered through private links. Private links are designed to reduce public discoverability, isolate access to a specific experience, support encrypted delivery, and allow senders to share directly with recipients.",
        "Private links are not the same as absolute identity verification. Unless additional protections are enabled, anyone who receives the private link may be able to open the experience.",
        "Users should share private links carefully and only with intended recipients.",
      ],
    },
    {
      id: "media-likeness",
      title: "10. Photos, Images, Videos, and Likeness",
      body: [
        "Users may upload photographs, images, videos, or other media into LRNSY experiences.",
        "By uploading media, you represent that you own the media or have permission to use it, and that the media does not violate privacy rights, intellectual property rights, dignity, safety, or applicable law.",
      ],
    },
    {
      id: "recordings",
      title: "11. Reaction Recording Features",
      body: [
        "LRNSY may offer optional reaction recording, selfie recording, screen recording, split-screen memory output, replay capture, or similar features.",
        "These features are sensitive because they may capture a recipient’s image, voice, facial expression, environment, device screen, or emotional reaction.",
        "Recording should only begin after appropriate user action or consent. Secret or hidden recording is not permitted.",
      ],
    },
    {
      id: "cookies-storage",
      title: "12. Cookies and Browser Storage",
      body: [
        "LRNSY uses cookies and browser storage technologies in a privacy-conscious way. These technologies may include cookies, localStorage, sessionStorage, IndexedDB, browser caching, pixels, SDKs, or similar technologies.",
        "Essential storage supports authentication, secure session continuity, encrypted experience delivery, payment continuation, fraud prevention, recipient access protection, security enforcement, and infrastructure reliability.",
        "Functional storage may support theme preferences, saved drafts, locally cached encrypted links, dismissed campaign notifications, remembered archetype selections, incomplete experience restoration, and UI continuity preferences.",
        "Analytics and marketing technologies are optional where consent is required and should never intentionally process plaintext private letters or intimate emotional message content.",
      ],
    },
    {
      id: "analytics",
      title: "13. Analytics, Pixels, and Advertising Attribution",
      body: [
        "LRNSY may use analytics tools or platform pixels to understand which pages are visited, which campaigns perform, which archetypes generate interest, where users drop off, whether payments complete, whether recipient experiences are completed, and whether recipients become senders.",
        "LRNSY does not intend to send private letter text, sensitive emotional message content, or intimate experience content to advertising platforms for profiling.",
      ],
    },
    {
      id: "data-sharing",
      title: "14. Data Sharing",
      body: [
        "LRNSY may share personal data only where reasonably necessary, lawful, and consistent with this Privacy Policy.",
      ],
      bullets: [
        "Hosting providers.",
        "Database providers.",
        "Authentication providers.",
        "Payment processors.",
        "Cloud storage providers.",
        "Analytics providers.",
        "Security and fraud-prevention vendors.",
        "Customer support tools.",
        "Professional advisors.",
        "Regulators, law enforcement, or courts where legally required.",
        "Business partners where required to provide a specific feature.",
      ],
      note: "LRNSY does not sell private letters and does not intend to share private emotional message content with advertisers for behavioral profiling.",
    },
    {
      id: "retention-deletion",
      title: "15. Data Retention and Deletion",
      body: [
        "LRNSY retains personal data only for as long as reasonably necessary for the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law.",
        "Retention may depend on account status, payment records, legal obligations, tax obligations, fraud-prevention needs, security needs, dispute needs, user requests, and technical limitations.",
        "Users may request deletion or privacy support by contacting LRNSY at support@lrnsy.electrixitaty.com.",
      ],
    },
    {
      id: "user-responsibilities",
      title: "16. User Responsibilities",
      body: [
        "Users are responsible for writing content lawfully and respectfully, uploading only photos or media they have rights to use, sharing private links carefully, protecting passcodes, protecting decryption keys, protecting account credentials, avoiding harassment or abuse, avoiding non-consensual use of another person’s image or private information, and choosing appropriate privacy settings.",
      ],
    },
    {
      id: "children",
      title: "17. Children and Minors",
      body: [
        "LRNSY is not intended for unlawful or unsafe use by children. Users must not use LRNSY to exploit minors, harass minors, sexualize minors, manipulate minors, collect personal data from minors unlawfully, upload inappropriate images of minors, or send harmful or abusive content to minors.",
      ],
    },
    {
      id: "contact",
      title: "18. Contact",
      body: [
        `For privacy questions, user rights requests, deletion requests, complaints, data protection concerns, or support needs, contact LRNSY Privacy & Support at ${LRNSY_LEGAL.supportEmail}.`,
      ],
    },
  ],
};

export const TERMS_OF_SERVICE_DOCUMENT: LegalDocument = {
  slug: "terms",
  title: "LRNSY Terms of Service",
  subtitle:
    "The rules that govern access to LRNSY, creation of emotional experiences, payments, private links, user content, and platform safety.",
  effectiveDate: LRNSY_LEGAL.effectiveDate,
  lastUpdated: LRNSY_LEGAL.lastUpdated,
  sections: [
    {
      id: "introduction",
      title: "1. Introduction",
      body: [
        "Welcome to LRNSY.",
        "LRNSY is an emotionally immersive digital experience platform operated by Electrixitaty Limited. The platform allows users to create, personalize, purchase, send, receive, open, reply to, replay, and interact with emotionally guided digital experiences.",
        "By accessing or using LRNSY, you acknowledge that you have read, understood, and agreed to these Terms of Service and all related policies, including the LRNSY Privacy Policy and Cookie and Browser Storage Framework.",
        "If you do not agree to these Terms, you must not access or use LRNSY.",
      ],
    },
    {
      id: "legal-operator",
      title: "2. Legal Operator",
      body: [
        "LRNSY is owned and operated by Electrixitaty Limited, Nairobi, Kenya.",
        "All references to “LRNSY,” “we,” “our,” or “us” refer to Electrixitaty Limited and the LRNSY platform unless otherwise stated.",
      ],
    },
    {
      id: "definitions",
      title: "3. Definitions",
      body: [
        "“Platform” means the LRNSY website, application, systems, APIs, infrastructure, interfaces, services, tools, campaigns, and related technologies.",
        "“User” means any person who accesses or uses LRNSY.",
        "“Sender” means a user who creates, purchases, customizes, publishes, or shares an experience.",
        "“Recipient” means a person who receives, opens, views, interacts with, replies to, or accesses an experience.",
        "“Experience” means any emotional digital experience created through LRNSY.",
        "“User Content” means all content submitted through LRNSY including letters, names, photos, replies, recordings, reactions, videos, messages, uploads, captions, private notes, audio, symbolic gifting notes, or related content.",
      ],
    },
    {
      id: "eligibility",
      title: "4. Eligibility",
      body: [
        "You may use LRNSY only if you are legally capable of entering binding agreements, your use complies with applicable laws, and you are at least 18 years old or have lawful guardian consent where permitted by law.",
        "LRNSY may suspend, restrict, or terminate access where eligibility requirements are not met.",
      ],
    },
    {
      id: "nature-service",
      title: "5. Nature of the Service",
      body: [
        "LRNSY provides tools for emotional expression and emotionally immersive digital interaction.",
        "LRNSY does not guarantee emotional healing, reconciliation, forgiveness, romantic success, emotional outcomes, recipient responses, emotional reciprocity, relationship improvement, or social outcomes.",
        "LRNSY is not therapy, counselling, psychiatric treatment, crisis support, legal advice, financial advice, emergency infrastructure, or medical treatment.",
      ],
    },
    {
      id: "accounts",
      title: "6. User Accounts",
      body: [
        "Certain LRNSY features may require an account. You agree to provide accurate information, maintain account security, protect login credentials, restrict unauthorized access, and remain responsible for activity under your account.",
        "You may not impersonate another person, create deceptive accounts, share accounts unlawfully, or bypass platform restrictions.",
      ],
    },
    {
      id: "acceptable-use",
      title: "7. Acceptable Use",
      body: [
        "You may not use LRNSY to harass, threaten, manipulate, intimidate, blackmail, stalk, exploit, impersonate, abuse, distribute unlawful content, violate privacy rights, distribute non-consensual intimate content, target minors unlawfully, distribute malware, conduct fraud, conduct phishing, bypass platform security, scrape data, or interfere with platform operations.",
        "You may not use LRNSY for revenge, coercion, emotional extortion, intimidation campaigns, or unlawful surveillance.",
      ],
    },
    {
      id: "user-content",
      title: "8. User Content",
      body: [
        "Users retain ownership of their User Content.",
        "By using LRNSY, users grant LRNSY limited rights necessary to operate the platform, process experiences, render experiences, secure infrastructure, maintain systems, deliver recipient experiences, process encrypted content, improve services, investigate abuse, comply with law, and enforce platform safety.",
        "LRNSY does not claim ownership over private emotional letters.",
        "Users acknowledge that recipients may screenshot experiences, record screens, copy content, forward links, share experiences, or externally distribute content. LRNSY cannot fully prevent recipient-side redistribution.",
      ],
    },
    {
      id: "privacy-encryption",
      title: "9. Privacy and Encryption",
      body: [
        "LRNSY is intentionally designed around privacy-conscious emotional interaction. The platform may use encryption systems, client-side encryption, private recipient links, restricted access controls, privacy isolation, tokenized access systems, optional Ultra Private Mode, and a minimal tracking philosophy.",
        "No system is perfectly secure. Recipients may redistribute content, compromised devices may expose content, screenshots may occur, and users share content at their own discretion.",
      ],
    },
    {
      id: "private-links",
      title: "10. Private Links",
      body: [
        "Recipient links are intended for private sharing. Users are responsible for protecting recipient links, sharing links intentionally, managing access responsibly, and understanding that forwarded links may expose experiences.",
        "LRNSY cannot guarantee that recipients will maintain confidentiality.",
      ],
    },
    {
      id: "payments",
      title: "11. Payments",
      body: [
        "Certain features require payment. Users agree to provide lawful payment methods, authorize transactions, comply with payment provider rules, avoid fraudulent disputes, and avoid chargeback abuse.",
        "Payments may be processed through third-party providers including payment processors, banks, card processors, mobile money providers, or financial infrastructure partners.",
        "LRNSY does not store full payment card information directly unless explicitly stated.",
      ],
    },
    {
      id: "refunds",
      title: "12. Refunds",
      body: [
        "Because LRNSY experiences are highly personalized and emotionally customized, completed experiences may not be refundable, delivered experiences may not be reversible, and partially consumed experiences may not qualify for refunds.",
        "Refunds may be considered where technical failure occurred, duplicate payment occurred, unlawful transactions occurred, or applicable law requires refunds.",
      ],
    },
    {
      id: "symbolic-gifting",
      title: "13. Symbolic Gifting",
      body: [
        "Petals, bouquets, roses, and related features are symbolic emotional systems. Unless expressly stated otherwise, they are not currency, investments, securities, savings products, guaranteed redeemable assets, or financial instruments.",
        "LRNSY may modify, limit, suspend, remove, or redesign symbolic gifting systems at any time.",
      ],
    },
    {
      id: "programs",
      title: "14. Creator, Ambassador, and Campaign Programs",
      body: [
        "LRNSY may operate creator programs, ambassador systems, referral systems, production collaborations, marketing partnerships, campaign systems, and content programs.",
        "Participation may require separate agreements. LRNSY may approve or reject participants, suspend access, remove participants, modify commissions, change payout structures, revise program requirements, or discontinue programs.",
      ],
    },
    {
      id: "ip",
      title: "15. Intellectual Property",
      body: [
        "LRNSY retains ownership of platform infrastructure, UI systems, animations, emotional archetype systems, emotional interaction systems, design systems, workflows, campaign systems, software, source code, trademarks, branding, logos, interfaces, proprietary logic, and related intellectual property.",
        "Users may not copy, scrape, reverse engineer, reproduce, clone, resell, exploit, or commercially replicate LRNSY systems without authorization.",
      ],
    },
    {
      id: "availability",
      title: "16. Platform Availability",
      body: [
        "LRNSY may modify features, redesign systems, pause campaigns, discontinue services, update interfaces, change pricing, update experiences, remove features, or restrict access.",
        "LRNSY does not guarantee uninterrupted availability.",
      ],
    },
    {
      id: "third-party-services",
      title: "17. Third-Party Services",
      body: [
        "LRNSY may integrate third-party services including payment processors, authentication providers, analytics providers, hosting providers, communication systems, media infrastructure, or cloud services.",
        "LRNSY is not responsible for third-party systems beyond applicable legal obligations.",
      ],
    },
    {
      id: "warranties",
      title: "18. Disclaimer of Warranties",
      body: [
        "LRNSY is provided “as is” and “as available.” To the maximum extent permitted by law, LRNSY disclaims warranties including uninterrupted availability, guaranteed delivery, perfect functionality, compatibility, uninterrupted access, emotional outcomes, emotional success, recipient behavior, reconciliation, forgiveness, or commercial results.",
      ],
    },
    {
      id: "liability",
      title: "19. Limitation of Liability",
      body: [
        "To the maximum extent permitted by law, Electrixitaty Limited, LRNSY, affiliates, directors, employees, contractors, creators, ambassadors, licensors, or partners shall not be liable for indirect damages, emotional disappointment, relationship outcomes, emotional misunderstandings, screenshots, forwarded content, unauthorized access, lost opportunities, device compromise, emotional distress, reputation loss, campaign performance, third-party misconduct, or recipient actions.",
        "Nothing in these Terms excludes liability where exclusion is unlawful.",
      ],
    },
    {
      id: "indemnification",
      title: "20. Indemnification",
      body: [
        "You agree to indemnify and hold harmless LRNSY and Electrixitaty Limited from claims, damages, liabilities, losses, costs, and expenses arising from your use of LRNSY, your User Content, your violations of these Terms, unlawful conduct, infringement claims, privacy violations, or misuse of the platform.",
      ],
    },
    {
      id: "termination",
      title: "21. Termination",
      body: [
        "LRNSY may suspend or terminate access where users violate these Terms, abuse the platform, create legal risk, create operational risk, engage in fraud, violate laws, or compromise platform safety.",
        "Users may stop using LRNSY at any time.",
      ],
    },
    {
      id: "governing-law",
      title: "22. Governing Law",
      body: [
        "These Terms are governed by the laws of Kenya unless mandatory law requires otherwise. Disputes may be subject to Kenyan jurisdiction and courts unless applicable law requires a different forum.",
      ],
    },
    {
      id: "contact",
      title: "23. Contact",
      body: [
        `For legal, privacy, or support questions, contact LRNSY at ${LRNSY_LEGAL.supportEmail}.`,
      ],
    },
  ],
};
